/**
 * Normalize GET .../alo-groups/:id/alos payloads (campaign + foundations).
 * Backend stores omissions as type=omission but historically labels the bucket `differentiators`.
 */

export function normalizeAloType(raw: unknown): 'asset' | 'liability' | 'omission' | null {
    const s = String(raw || '')
        .trim()
        .toLowerCase()
    if (s === 'asset' || s === 'assets' || s === 'attractant' || s === 'attractants') return 'asset'
    if (s === 'liability' || s === 'liabilities') return 'liability'
    if (
        s === 'omission' ||
        s === 'omissions' ||
        s === 'differentiator' ||
        s === 'differentiators'
    ) {
        return 'omission'
    }
    return null
}

export type ALOsByTypePayload = {
    attractants: unknown[]
    liabilities: unknown[]
    omissions: unknown[]
}

export function parseALOsByTypePayload(raw: unknown): ALOsByTypePayload {
    let bucket: Record<string, unknown> =
        raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}

    // Unwrap { success, data } (possibly nested once)
    for (let i = 0; i < 2; i++) {
        const inner = bucket.data
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
            bucket = inner as Record<string, unknown>
        }
    }

    const attractants = Array.isArray(bucket.attractants) ? bucket.attractants : []
    const liabilities = Array.isArray(bucket.liabilities) ? bucket.liabilities : []
    const omissions = Array.isArray(bucket.omissions)
        ? bucket.omissions
        : Array.isArray(bucket.differentiators)
          ? bucket.differentiators
          : []

    return { attractants, liabilities, omissions }
}

/** Unwrap POST .../alos create responses ({ success, data: CampaignALO[] }). */
export function parseAddALOsResponse(raw: unknown): unknown[] {
    if (Array.isArray(raw)) return raw
    const body = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
    const data = body.data
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const nested = (data as Record<string, unknown>).data
        if (Array.isArray(nested)) return nested
    }
    return []
}

export type AloPersuasionType = 'ethos' | 'pathos' | 'logos' | 'kairos' | 'other'

export type NormalizedAloForSave = {
    type: 'asset' | 'liability' | 'omission'
    category: string
    text: string
    description?: string
    title?: string
    isStrategic?: boolean
    priority?: number
    proof_points?: string[]
    proofUrl?: string
    proofStrength?: 'strong' | 'medium' | 'weak'
    persuasion_type?: AloPersuasionType
    rhetoric_devices?: string[]
    related_products?: string[]
    confidence?: number
}

const PERSUASION_TYPES = new Set<AloPersuasionType>(['ethos', 'pathos', 'logos', 'kairos', 'other'])

function normalizePersuasionType(raw: unknown): AloPersuasionType | undefined {
    const s = String(raw || '')
        .trim()
        .toLowerCase()
    return PERSUASION_TYPES.has(s as AloPersuasionType) ? (s as AloPersuasionType) : undefined
}

/** Coerce research/generation rows into a shape the save API accepts (type + non-empty text). */
export function normalizeAloForSave(alo: Record<string, unknown>): NormalizedAloForSave | null {
    const type = normalizeAloType(alo.type)
    if (!type) return null

    const text = String(
        alo.text || alo.title || alo.name || alo.headline || alo.label || alo.summary || ''
    )
        .trim()
        .slice(0, 255)
    if (!text) return null

    const confidenceRaw = alo.confidence
    const confidenceNum =
        typeof confidenceRaw === 'number' && Number.isFinite(confidenceRaw)
            ? confidenceRaw > 1
                ? confidenceRaw / 100
                : confidenceRaw
            : undefined

    return {
        type,
        category: String(alo.category || 'other').trim() || 'other',
        text,
        ...(typeof alo.title === 'string' && alo.title.trim() ? { title: alo.title.trim().slice(0, 255) } : {}),
        ...(typeof alo.description === 'string' && alo.description.trim()
            ? { description: alo.description.trim() }
            : {}),
        ...(typeof alo.isStrategic === 'boolean' ? { isStrategic: alo.isStrategic } : {}),
        ...(typeof alo.priority === 'number' ? { priority: alo.priority } : {}),
        ...(Array.isArray(alo.proof_points) ? { proof_points: alo.proof_points as string[] } : {}),
        ...(typeof alo.proofUrl === 'string' && alo.proofUrl.trim() ? { proofUrl: alo.proofUrl.trim() } : {}),
        ...(alo.proofStrength === 'strong' || alo.proofStrength === 'medium' || alo.proofStrength === 'weak'
            ? { proofStrength: alo.proofStrength }
            : {}),
        ...(normalizePersuasionType(alo.persuasion_type)
            ? { persuasion_type: normalizePersuasionType(alo.persuasion_type) }
            : {}),
        ...(Array.isArray(alo.rhetoric_devices) ? { rhetoric_devices: alo.rhetoric_devices as string[] } : {}),
        ...(Array.isArray(alo.related_products) ? { related_products: alo.related_products as string[] } : {}),
        ...(confidenceNum !== undefined ? { confidence: confidenceNum } : {}),
    }
}

export function normalizeAloListForSave(alos: ReadonlyArray<object>): NormalizedAloForSave[] {
    return alos
        .map(alo => normalizeAloForSave(alo as Record<string, unknown>))
        .filter((a): a is NormalizedAloForSave => a != null)
}
